import { useEffect, useState, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertCircle, Save, Info } from "lucide-react";
import { ProductEntry, FootprintProduct } from "@/types/product/productStep.types";
import { useData, useDataWithFilter } from "@/hooks/useData";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { CommonFormFields } from "./shared/CommonFormFields";
import { TransportLegsField } from "./shared/TransportLegsField";
import { normalizeProductTransports } from "./shared/transportUtils";
import { useProductFormValidation } from "./shared/useProductFormValidation";
import {
  calculateExtractionProductFootprints,
  calculateProductRevenue,
  calculateRatioByQuantity,
  calculateRatioByRevenue,
} from "@/components/footprints/utils/calculations/extraction";
import { convertToTonnes } from "@/components/footprints/utils/calculations/unitConversions";
import { calculateTransformationProductFootprints } from "@/components/footprints/utils/calculations/transformation";
import { MaterialDocWithId } from "@/types/material";

interface MixedActivityProductFormProps {
  formEntry: ProductEntry;
  onSave: (entry: ProductEntry) => void;
  onClose: () => void;
  editing: boolean;
  generalStoneTypeId?: string;
  calculationInputs: any;
  allEntries: ProductEntry[];
  totalAnnualProduction: number;
  totalRevenue: number;
  formData?: any; // Add formData prop to access annual consumption data
  extractionProducts: ProductEntry[]; // Local extraction products
  currentCompanyId?: string; // Current company ID
}

export default function MixedActivityProductForm({
  formEntry: formEntryProp,
  onSave,
  onClose,
  editing,
  generalStoneTypeId,
  calculationInputs,
  allEntries,
  totalAnnualProduction,
  totalRevenue,
  formData,
  extractionProducts,
  currentCompanyId,
}: MixedActivityProductFormProps) {
  const { t } = useLanguage();

  const [formEntry, setFormEntry] = useState<ProductEntry>(() =>
    normalizeProductTransports({
      ...formEntryProp,
      productFlow: formEntryProp.productFlow || "extraction", // Ensure productFlow is always set
    })
  );

  useEffect(() => {
    setFormEntry(normalizeProductTransports(formEntryProp));
  }, [formEntryProp]);

  // Ensure productFlow is set correctly for mixed activity companies
  useEffect(() => {
    if (formEntry.productFlow !== formEntryProp.productFlow) {
      setFormEntry((prev) => ({
        ...prev,
        productFlow: formEntryProp.productFlow || "extraction", // Default to extraction if not set
      }));
    }
  }, [formEntryProp.productFlow, formEntry.productFlow]);

  // Use correct validation type based on product flow
  const validationType = formEntry.productFlow === "extraction" ? "extraction" : "extraction_and_transformation";
  const { touched, errors, validateForm, handleFieldBlur, clearFieldError, isFormValid, markAllFieldsTouched } =
    useProductFormValidation(validationType);

  // Fetch stone types for the dropdown
  const { data: stoneTypes } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  // Fetch specific stone type if we have a generalStoneTypeId
  const { document: generalStoneType } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
    documentId: generalStoneTypeId || "",
  });

  // Fetch companies for transformation origin selection
  const { data: companies } = useData<any>({
    collectionName: "companies",
  });

  // Fetch transports
  const { data: transportDistributions } = useData<any>({
    collectionName: "setup_transportDistributions",
  });

  // Fetch footprints for the selected company
  const { data: companyFootprints } = useDataWithFilter<any>({
    collectionName: "footprints",
    filters: formEntry.originCompanyId
      ? [{ field: "company_id", operator: "==", value: formEntry.originCompanyId }]
      : undefined,
  });

  const footprintIds = useMemo(() => {
    return companyFootprints?.map((footprint) => footprint.id) || [];
  }, [companyFootprints]);

  const { data: availableOriginProducts } = useDataWithFilter<FootprintProduct>({
    collectionName: "footprint_products",
    filters:
      footprintIds.length > 0 && formEntry.stoneType
        ? [
            { field: "footprint_id", operator: "in", value: footprintIds },
            { field: "stone_type_id", operator: "==", value: formEntry.stoneType },
            { field: "product_flow", operator: "==", value: "extraction" },
          ]
        : undefined,
  });

  // Merge local extraction products with Firestore products
  const mergedOriginProducts = useMemo(() => {
    const firestoreProducts = availableOriginProducts || [];

    // Filter local extraction products that match the selected stone type
    const localProducts = (extractionProducts || [])
      .filter((product) => product.stoneType === formEntry.stoneType)
      .map((product) => ({
        id: product.id,
        name: product.name,
        product_type: product.productType,
        functional_unit: product.functionalUnit,
        stone_type_id: product.stoneType,
        stone_type_name: product.stoneTypeName,
        product_flow: product.productFlow as "extraction",
        isLocal: true, // Mark as local/unsaved
      }));

    // Combine Firestore and local products
    const combinedProducts = [...firestoreProducts];
    localProducts.forEach((localProduct) => {
      const existsInFirestore = firestoreProducts.some((fp) => fp.id === localProduct.id);
      if (!existsInFirestore) {
        combinedProducts.push(localProduct as any);
      }
    });

    return combinedProducts;
  }, [availableOriginProducts, extractionProducts, formEntry.stoneType]);

  // Filter active data
  const activeStoneTypes = stoneTypes || [];
  const activeCompanies = (companies || []).filter((company: any) => !company.isArchived);
  const activeTransportDistributions = (transportDistributions || []).filter((transport: any) => !transport.isArchived);

  // Filter companies by activity type (only extraction companies for stone origin)
  // Exclude current company from the list
  const extractionCompanies = activeCompanies.filter(
    (company: any) =>
      company.id !== currentCompanyId &&
      (company.activityType === "extraction" || company.activityType === "extraction_and_transformation")
  );

  // Get stone type data
  const selectedStoneType: StoneType | null =
    activeStoneTypes.find((st) => st.id === formEntry.stoneType) ||
    (generalStoneTypeId && generalStoneType ? generalStoneType : null);

  // Get stone density
  const stoneDensity = selectedStoneType?.dens_pedra?.value || 0;

  // Update density when stone type changes
  useEffect(() => {
    if (formEntry.density !== stoneDensity) {
      setFormEntry((prev) => ({
        ...prev,
        density: stoneDensity,
      }));
    }
  }, [stoneDensity, formEntry.density]);

  // Default to extraction stone type if available
  useEffect(() => {
    if (generalStoneTypeId && !formEntry.stoneType && activeStoneTypes.length > 0) {
      const selectedStone = activeStoneTypes.find((st) => st.id === generalStoneTypeId);

      setFormEntry((prev) => ({
        ...prev,
        stoneType: generalStoneTypeId,
        stoneTypeName: selectedStone?.name || "",
      }));
    }
  }, [generalStoneTypeId, activeStoneTypes.length, formEntry.stoneType]);

  // Ensure stone type name is populated for existing entries
  useEffect(() => {
    if (formEntry.stoneType && !formEntry.stoneTypeName && activeStoneTypes.length > 0) {
      const selectedStone = activeStoneTypes.find((st) => st.id === formEntry.stoneType);
      if (selectedStone && selectedStone.name !== formEntry.stoneTypeName) {
        setFormEntry((prev) => ({
          ...prev,
          stoneTypeName: selectedStone.name,
        }));
      }
    }
  }, [activeStoneTypes.length, formEntry.stoneType, formEntry.stoneTypeName]);

  // Automatically set comesFromAssociated to "no" when external stone is selected
  useEffect(() => {
    if (formEntry.usesExternalStone === "yes" && formEntry.comesFromAssociated !== "no") {
      setFormEntry((prev) => ({
        ...prev,
        comesFromAssociated: "no",
      }));
    }
  }, [formEntry.usesExternalStone]);

  // When transformation product's stone type differs from the company's extraction
  // stone type, force average-factors path: hide the external-stone question,
  // preselect "yes", and clear any internal origin product selection.
  // When switching back to matching stone type, clear the auto-set value so the
  // radios reappear empty and the user must pick again.
  const autoSetExternalRef = useRef(false);
  useEffect(() => {
    if (formEntry.productFlow !== "transformation") return;
    if (!generalStoneTypeId || !formEntry.stoneType) return;

    const stoneTypesDiffer = formEntry.stoneType !== generalStoneTypeId;

    if (stoneTypesDiffer) {
      if (formEntry.usesExternalStone !== "yes" || formEntry.originProductId) {
        autoSetExternalRef.current = true;
        setFormEntry((prev) => ({
          ...prev,
          usesExternalStone: "yes",
          originProductId: "",
        }));
      } else {
        autoSetExternalRef.current = true;
      }
    } else if (autoSetExternalRef.current) {
      autoSetExternalRef.current = false;
      setFormEntry((prev) => ({
        ...prev,
        usesExternalStone: "",
      }));
    }
  }, [
    formEntry.productFlow,
    formEntry.stoneType,
    generalStoneTypeId,
    formEntry.usesExternalStone,
    formEntry.originProductId,
  ]);

  // Clear origin product when company changes
  useEffect(() => {
    if (formEntry.originProductId && Array.isArray(mergedOriginProducts) && mergedOriginProducts.length > 0) {
      const stillExists = mergedOriginProducts.some(
        (product) => String(product.id) === String(formEntry.originProductId)
      );
      if (!stillExists) {
        setFormEntry((prev) => ({
          ...prev,
          originProductId: "",
        }));
      }
    }
  }, [formEntry.originCompanyId, mergedOriginProducts]);

  // Clear errors when form state changes and fields become valid
  useEffect(() => {
    // Clear originProductId error if originCompanyId is cleared
    if (!formEntry.originCompanyId && errors.originProductId) {
      clearFieldError("originProductId", "", formEntry);
    }
  }, [formEntry.originCompanyId, errors.originProductId, clearFieldError, formEntry]);

  // Get selected stone type name for display
  const selectedStoneTypeName = selectedStoneType?.name || "";

  // Transformation product uses a stone type different from the company's
  // extraction stone type -> external/average-factors path is forced.
  const stoneTypeDiffersFromExtraction =
    !!generalStoneTypeId && !!formEntry.stoneType && formEntry.stoneType !== generalStoneTypeId;

  const handleInputChange = (field: string, value: any) => {
    if (field === "stoneType") {
      // For mixed companies, stone type is still locked to the general stone type
      // But we allow the transformation flow to proceed
      const selectedStone = activeStoneTypes.find((st) => st.id === value);
      setFormEntry((prev) => ({
        ...prev,
        stoneType: value,
        stoneTypeName: selectedStone?.name || "",
      }));
    } else if (field === "originCompanyId") {
      // Clear origin product when company changes
      setFormEntry((prev) => ({
        ...prev,
        [field]: value,
        originProductId: "",
      }));
    } else {
      setFormEntry((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error immediately when user selects a value
    if (value && value.trim() !== "") {
      // Use setTimeout to ensure the form state has updated
      setTimeout(() => {
        clearFieldError(field, value, { ...formEntry, [field]: value });
      }, 0);
    }
  };

  // Extract available material IDs from annual consumption data based on product flow
  const getAvailableMaterialIds = (): string[] => {
    if (!formData) return [];

    const activityType = calculationInputs?.generalData?.activityType;
    let materialEntries: any[] = [];

    if (activityType === "extraction_and_transformation") {
      // Mixed company - check materials based on product flow
      if (formEntry.productFlow === "extraction") {
        materialEntries = formData.annualConsumptions?.extraction?.materials?.entries || [];
      } else if (formEntry.productFlow === "transformation") {
        materialEntries = formData.annualConsumptions?.transformation?.materials?.entries || [];
      }
    } else {
      // Single activity company - check both possible structures
      if (formEntry.productFlow === "extraction") {
        materialEntries =
          formData.materials?.entries || formData.annualConsumptions?.extraction?.materials?.entries || [];
      } else if (formEntry.productFlow === "transformation") {
        materialEntries =
          formData.materials?.entries || formData.annualConsumptions?.transformation?.materials?.entries || [];
      }
    }

    // Extract material IDs from material entries (check multiple possible field names)
    const materialIds = materialEntries
      .filter((entry: any) => {
        // Only include entries that have a material type/name (actual materials, not empty entries)
        return entry.materialType && entry.materialType.trim() !== "";
      })
      .map((entry: any) => {
        // For materials, we need to find the setup_item_id from the material name
        // This will be resolved when we fetch the material document
        return entry.materialType;
      });

    return materialIds;
  };

  const { data: materialDocs } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });
  const { data: stoneDocs } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  const handleSave = () => {
    markAllFieldsTouched(["transports"]);
    if (validateForm(formEntry) && materialDocs && stoneDocs && transportDistributions) {
      // Calculate ratios based on the current phase
      const calculatedProductRevenue = calculateProductRevenue(
        formEntry.annualProduction,
        totalRevenue,
        totalAnnualProduction
      );

      // Convert current product's production to tonnes for accurate ratio calculation
      const productionInTonnes = convertToTonnes(formEntry).value;
      const ratioByQuantity = calculateRatioByQuantity(productionInTonnes, totalAnnualProduction);

      const ratioByRevenue = calculateRatioByRevenue(formEntry.productRevenue, totalRevenue);

      // Auto-allocate material quantities based on annual consumption and product allocation ratios
      // Helper to find annual material entry and products using a material
      const getAnnualMaterialData = (material: any) => {
        let materialEntries: any[] = [];

        if (formEntry.productFlow === "extraction") {
          materialEntries = formData?.annualConsumptions?.extraction?.materials?.entries || [];
        } else if (formEntry.productFlow === "transformation") {
          materialEntries = formData?.annualConsumptions?.transformation?.materials?.entries || [];
        }

        if (materialEntries.length === 0) {
          if (formEntry.productFlow === "extraction") {
            materialEntries = formData?.materials?.entries || [];
          } else if (formEntry.productFlow === "transformation") {
            materialEntries = formData?.transformationMaterials?.entries || formData?.materials?.entries || [];
          }
        }

        const annualMaterialEntry = materialEntries.find((entry: any) => entry.materialType === material.materialName);
        const entriesForAllocation = editing ? allEntries : [...allEntries, formEntry];
        const productsUsingMaterial = entriesForAllocation.filter(
          (entry) =>
            entry.productFlow === formEntry.productFlow &&
            entry.materialsUsed.some((mat) => mat.materialName === material.materialName)
        );

        return { annualMaterialEntry, productsUsingMaterial };
      };

      // Carbon allocation type: extraction uses physical, transformation uses economic
      const allocationType = formEntry.productFlow === "extraction" ? "physical" : "economic";

      // Allocate materials for carbon (rd-based allocation)
      const materialsWithQuantities = formEntry.materialsUsed.map((material) => {
        if (editing && material.quantity > 0) {
          return material;
        }

        const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

        if (annualMaterialEntry && annualMaterialEntry.quantity) {
          if (productsUsingMaterial.length === 0) {
            return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
          }

          let totalForMaterial = 0;
          let currentProductValue = 0;

          if (allocationType === "physical") {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + convertToTonnes(entry).value, 0);
            currentProductValue = productionInTonnes;
          } else {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
            currentProductValue = Number(formEntry.productRevenue) || 0;
          }

          const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
          const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

          return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
        }
        return material;
      });

      // Allocate materials for hydric (ALWAYS economic allocation)
      const materialsUsedHydric = allocationType === "economic"
        ? materialsWithQuantities // Same as carbon when already economic
        : formEntry.materialsUsed.map((material) => {
            const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

            if (annualMaterialEntry && annualMaterialEntry.quantity) {
              if (productsUsingMaterial.length === 0) {
                return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
              }

              const totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
              const currentProductValue = Number(formEntry.productRevenue) || 0;
              const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
              const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

              return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
            }
            return material;
          });

      const entryWithRatios = {
        ...formEntry,
        materialsUsed: materialsWithQuantities,
        materialsUsedHydric,
        calculatedProductRevenue,
        ratioByQuantity,
        ratioByRevenue,
      };

      // Select the correct consumptions object based on the product's flow
      const consumptionsRoot = calculationInputs.consumptions || {};
      let consumptionsForPhase =
        formEntry.productFlow === "extraction" ? consumptionsRoot.extraction : consumptionsRoot.transformation;

      // For single-activity companies, consumptions are not nested.
      // If `consumptionsForPhase` is undefined, it means we are in a single-activity flow.
      if (!consumptionsForPhase) {
        consumptionsForPhase = consumptionsRoot;
      }

      // Temporary workaround: If consumption data is missing but we have materials in the product,
      // provide minimal consumption structure to enable basic calculations
      const hasAnyConsumptionData =
        consumptionsForPhase &&
        ((consumptionsForPhase.materials &&
          (consumptionsForPhase.materials.total_pc_mat > 0 || consumptionsForPhase.materials.total_ph_mat > 0)) ||
          (consumptionsForPhase.electricity && consumptionsForPhase.electricity.total_pc_ele > 0) ||
          (consumptionsForPhase.fuels && consumptionsForPhase.fuels.total_pc_comb > 0) ||
          (consumptionsForPhase.residue && consumptionsForPhase.residue.total_pc_res > 0));

      if (!hasAnyConsumptionData && formEntry.materialsUsed && formEntry.materialsUsed.length > 0) {
        // Provide minimal consumption structure to enable material calculations
        consumptionsForPhase = {
          materials: { total_pc_mat: 1, total_ph_mat: 1 }, // Minimal non-zero values
          electricity: { total_pc_ele: 0, total_ph_ele: 0 },
          fuels: { total_pc_comb: 0, total_ph_comb: 0 },
          residue: { total_pc_res: 0 },
          water: { total_ph_agua: 0 },
        };
      }

      // Create a new calculationInputs object with the flattened consumptions for the current phase
      const calculationInputsForPhase = {
        ...calculationInputs,
        consumptions: consumptionsForPhase,
      };

      // Calculate footprints based on the current phase
      const footprints =
        formEntry.productFlow === "extraction"
          ? calculateExtractionProductFootprints(
              entryWithRatios,
              allEntries,
              calculationInputsForPhase,
              materialDocs,
              stoneDocs
            )
          : calculateTransformationProductFootprints(
              entryWithRatios,
              allEntries,
              calculationInputsForPhase,
              stoneDocs,
              transportDistributions,
              materialDocs
            );

      // Transport applies only to transformation products. Derive the aggregate
      // (summed over all legs) straight from the calculation result so the saved
      // value is correct without depending on a later recalc effect — matching
      // TransformationProductForm.
      const pcTransTotal =
        formEntry.productFlow === "transformation" ? (footprints as any).pc_trans_total || 0 : 0;
      const phTransTotal =
        formEntry.productFlow === "transformation" ? (footprints as any).ph_trans_total || 0 : 0;

      const mappedEntry = {
        ...entryWithRatios,
        ...footprints,
        rendimento: calculationInputs.generalData.rend,
        ph_trans: phTransTotal,
        pc_trans: pcTransTotal,
        carbonFootprintTransport: pcTransTotal,
        hydricFootprintTransport: phTransTotal,
        // Carbon phase-specific keys
        carbonFootprintMaterials:
          formEntry.productFlow === "extraction"
            ? (footprints as any).pc_ext_materiais
            : (footprints as any).pc_trsf_materiais,
        carbonFootprintElectricity:
          formEntry.productFlow === "extraction" ? (footprints as any).pc_ext_ecr : (footprints as any).pc_trsf_ecr,
        carbonFootprintElectricityOnly: (footprints as any).pc_ele || 0,
        carbonFootprintFuels: (footprints as any).pc_comb || 0,
        carbonFootprintResidue: (footprints as any).pc_res || 0,
        carbonFootprintTotal:
          formEntry.productFlow === "extraction"
            ? (footprints as any).pc_prod
            : (footprints as any).carbonFootprintTotal,

        // Hydric phase-specific keys
        hydricFootprintMaterials:
          formEntry.productFlow === "extraction"
            ? (footprints as any).ph_ext_materiais
            : (footprints as any).ph_trsf_materiais,
        hydricFootprintElectricity:
          formEntry.productFlow === "extraction" ? (footprints as any).ph_ext_ec : (footprints as any).ph_trsf_ec,
        hydricFootprintWater:
          formEntry.productFlow === "extraction" ? (footprints as any).ph_ext_agua : (footprints as any).ph_trsf_agua,
        hydricFootprintTotal:
          formEntry.productFlow === "extraction"
            ? (footprints as any).ph_prod
            : (footprints as any).hydricFootprintTotal,

        // Detailed keys used in the summary card
        ...(formEntry.productFlow === "extraction"
          ? {
              pc_ext_materiais: (footprints as any).pc_ext_materiais,
              pc_ext_ecr: (footprints as any).pc_ext_ecr,
              pc_prod: (footprints as any).pc_prod,
              ph_ext_materiais: (footprints as any).ph_ext_materiais,
              ph_ext_ec: (footprints as any).ph_ext_ec,
              ph_ext_agua: (footprints as any).ph_ext_agua,
              ph_prod: (footprints as any).ph_prod,
            }
          : {
              // Transformation phase values
              pc_trsf_materiais: (footprints as any).pc_trsf_materiais,
              pc_trsf_ecr: (footprints as any).pc_trsf_ecr,
              pc_trsf_total: (footprints as any).pc_trsf_total,
              ph_trsf_materiais: (footprints as any).ph_trsf_materiais,
              ph_trsf_ec: (footprints as any).ph_trsf_ec,
              ph_trsf_agua: (footprints as any).ph_trsf_agua,
              ph_trsf_total: (footprints as any).ph_trsf_total,

              // Upstream values (extraction + transport)
              pc_ext_total: (footprints as any).pc_ext_total,
              pc_trans_total: (footprints as any).pc_trans_total,
              ph_ext_total: (footprints as any).ph_ext_total,
              ph_trans_total: (footprints as any).ph_trans_total,
            }),
      };

      onSave(mappedEntry);
    }
  };

  const onFieldBlur = (field: string) => {
    handleFieldBlur(field, formEntry);
  };

  return (
    <div>
      <div className="space-y-6 py-4 px-6">
        {/* Stone Type */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="stoneType" className={`font-medium ${errors.stoneType ? "text-red-500" : "text-gray-700"}`}>
              {formEntry.productFlow === "transformation"
                ? t("footprints.generalData.stoneTypeTransformation")
                : t("footprints.generalData.stoneType")}{" "}
              *
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("products.mixedActivityStoneTypeTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          {/* For transformation products: allow selecting any stone type */}
          {/* For extraction products: use the general stone type (disabled) */}
          {formEntry.productFlow === "transformation" ? (
            <SearchableSelect
              value={formEntry.stoneType}
              onChange={(value) => handleInputChange("stoneType", value)}
              onBlur={() => onFieldBlur("stoneType")}
              options={activeStoneTypes.map((stoneType) => ({
                value: stoneType.id,
                label: stoneType.name,
              }))}
              placeholder={t("footprints.generalData.selectStoneType")}
              className={touched.stoneType && errors.stoneType ? "border-red-500" : ""}
            />
          ) : (
            <Input value={selectedStoneTypeName} disabled className="h-11 bg-gray-50 text-gray-600 cursor-not-allowed" />
          )}
          
          {touched.stoneType && errors.stoneType && (
            <div className="flex items-center gap-1 text-red-500 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{errors.stoneType}</span>
            </div>
          )}
          <p className="text-xs text-gray-500">{t("products.mixedActivityStoneTypeNote")}</p>
        </div>

        {/* Mixed Activity Explanation */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
            <h4 className="text-sm font-medium text-purple-800">{t("products.mixedActivityData")}</h4>
          </div>
          <p className="text-sm text-purple-700">{t("products.mixedActivityExplanation")}</p>
        </div>

        {/* Transformation-specific fields for mixed companies */}
        {formEntry.productFlow === "transformation" && (
          <div className="space-y-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <h4 className="text-sm font-medium text-blue-800">{t("products.transformationData")}</h4>
            </div>

            {/* When transformation stone type differs from the company's extraction
                stone type, skip the external-stone question: average factors are
                forced automatically (see effect that preselects usesExternalStone="yes"). */}
            {!stoneTypeDiffersFromExtraction && (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <div className="w-1 h-4 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                  <div className="flex-1">
                    <Label className="font-medium text-gray-800 text-base">{t("products.usesExternalStone")} *</Label>
                  </div>
                </div>
                <div className="flex gap-6 ml-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="usesExternalStone"
                      value="yes"
                      checked={formEntry.usesExternalStone === "yes"}
                      onChange={(e) => handleInputChange("usesExternalStone", e.target.value)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{t("common.yes")}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="usesExternalStone"
                      value="no"
                      checked={formEntry.usesExternalStone === "no"}
                      onChange={(e) => handleInputChange("usesExternalStone", e.target.value)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{t("common.no")}</span>
                  </label>
                </div>
              </div>
            )}

            {/* If no external stone: Use internal extraction data */}
            {formEntry.usesExternalStone === "no" && (
              <>
                <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg ml-4 mb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        {t("products.willUseInternalExtractionData")}
                      </p>
                      <p className="text-xs text-green-600 mt-1">{t("products.mixedActivityInternalExplanation")}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 ml-4">
                  <Label className="font-medium text-gray-700">{t("products.selectOriginProduct")} *</Label>
                                          {mergedOriginProducts && mergedOriginProducts.length > 0 ? (
                          <SearchableSelect
                            value={String(formEntry.originProductId) || ""}
                            onChange={(value) => handleInputChange("originProductId", value)}
                            onBlur={() => onFieldBlur("originProductId")}
                            options={mergedOriginProducts.map((product) => ({
                              value: String(product.id),
                              label: `${product.name} - ${product.product_type} (${product.functional_unit.quantity} ${product.functional_unit.unit})`,
                            }))}
                            placeholder={t("products.selectProduct")}
                            className={touched.originProductId && errors.originProductId ? "border-red-500" : ""}
                          />
                        ) : (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600">
                              {footprintIds.length === 0
                                ? t("products.noProductsRegistered")
                                : formEntry.stoneType
                                ? t("products.noProductsForStoneType", { stoneType: selectedStoneType?.name || "" })
                                : t("products.selectStoneTypeFirst")}
                            </p>
                          </div>
                        )}
                  {touched.originProductId && errors.originProductId && (
                    <div className="flex items-center gap-1 text-red-500 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errors.originProductId}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* If external stone - always show average factors warning */}
            {formEntry.usesExternalStone === "yes" && (
              <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg ml-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-yellow-800">{t("products.willUseAverageFactors")}</p>
                    <p className="text-xs text-yellow-600 mt-1">{t("products.willUseAverageFactorsDescription")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* CR#2: Hidden for now - keep code for future use */}
            {false && formEntry.usesExternalStone === "yes" && (
              <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl ml-4">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-5 bg-indigo-500 rounded-full mt-0.5 flex-shrink-0"></div>
                  <div className="flex-1">
                    <Label className="font-medium text-gray-800">{t("products.comesFromAssociatedCompany")} *</Label>
                    <p className="text-sm text-gray-600 mt-1">{t("products.comesFromAssociatedCompanyHelp")}</p>
                  </div>
                </div>
                <div className="flex gap-6 ml-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="comesFromAssociated"
                      value="yes"
                      checked={formEntry.comesFromAssociated === "yes"}
                      onChange={(e) => handleInputChange("comesFromAssociated", e.target.value)}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {t("common.yes")}
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="comesFromAssociated"
                      value="no"
                      checked={formEntry.comesFromAssociated === "no"}
                      onChange={(e) => handleInputChange("comesFromAssociated", e.target.value)}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {t("common.no")}
                    </span>
                  </label>
                </div>

                {/* If NO: Show average factors message */}
                {formEntry.comesFromAssociated === "no" && (
                  <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg ml-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-yellow-800">{t("products.willUseAverageFactors")}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* If YES: Show company and product selection */}
                {formEntry.comesFromAssociated === "yes" && (
                  <div className="space-y-4 ml-6">
                    {/* Company Selection */}
                    <div className="space-y-2">
                      <Label className="font-medium text-gray-700">{t("products.selectOriginCompany")} *</Label>
                      <SearchableSelect
                        value={formEntry.originCompanyId || ""}
                        onChange={(value) => handleInputChange("originCompanyId", value)}
                        onBlur={() => onFieldBlur("originCompanyId")}
                        options={extractionCompanies.map((company: any) => ({
                          value: company.id,
                          label: company.name,
                        }))}
                        placeholder={extractionCompanies.length > 0 ? t("products.selectCompany") : t("common.loading")}
                        disabled={extractionCompanies.length === 0}
                        className={touched.originCompanyId && errors.originCompanyId ? "border-red-500" : ""}
                      />
                      {touched.originCompanyId && errors.originCompanyId && (
                        <div className="flex items-center gap-1 text-red-500 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          <span>{errors.originCompanyId}</span>
                        </div>
                      )}
                      {extractionCompanies.length === 0 && (
                        <p className="text-xs text-gray-500">{t("products.noExtractionCompaniesAvailable")}</p>
                      )}
                    </div>

                    {/* Product Selection - Only show if company is selected */}
                    {formEntry.originCompanyId && (
                      <div className="space-y-2">
                        <Label className="font-medium text-gray-700">{t("products.selectOriginProduct")} *</Label>
                        {mergedOriginProducts && mergedOriginProducts.length > 0 ? (
                          <SearchableSelect
                            value={String(formEntry.originProductId) || ""}
                            onChange={(value) => handleInputChange("originProductId", value)}
                            onBlur={() => onFieldBlur("originProductId")}
                            options={mergedOriginProducts.map((product) => ({
                              value: String(product.id),
                              label: `${product.name} - ${product.product_type} (${product.functional_unit.quantity} ${product.functional_unit.unit})`,
                            }))}
                            placeholder={t("products.selectProduct")}
                            className={touched.originProductId && errors.originProductId ? "border-red-500" : ""}
                          />
                        ) : (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600">
                              {footprintIds.length === 0
                                ? t("products.noProductsRegistered")
                                : formEntry.stoneType
                                ? t("products.noProductsForStoneType", { stoneType: selectedStoneType?.name || "" })
                                : t("products.selectStoneTypeFirst")}
                            </p>
                          </div>
                        )}
                        {touched.originProductId && errors.originProductId && (
                          <div className="flex items-center gap-1 text-red-500 text-sm">
                            <AlertCircle className="h-4 w-4" />
                            <span>{errors.originProductId}</span>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">{t("products.originProductHelp")}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Transport Information - Always show for transformation products */}
            {formEntry.productFlow === "transformation" && activeTransportDistributions !== undefined && (
                <div className="space-y-4 p-3 bg-white border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <h5 className="text-sm font-medium text-gray-700">{t("products.quarryTransformationTransport")}</h5>
                  </div>

                  <TransportLegsField
                    transports={formEntry.transports || []}
                    vehicleOptions={(activeTransportDistributions || []).map((tr: any) => ({
                      value: tr.vehicleType,
                      label: tr.vehicleType,
                    }))}
                    loading={!transportDistributions}
                    showError={!!touched.transports}
                    error={errors.transports}
                    onChange={(transports) => {
                      const updated = { ...formEntry, transports };
                      setFormEntry(updated);
                      if (touched.transports) clearFieldError("transports", transports, updated);
                    }}
                    onBlur={() => handleFieldBlur("transports", formEntry)}
                  />
                </div>
              )}
          </div>
        )}

        {/* Common Form Fields */}
        {(formEntry.productFlow === "extraction" || !formEntry.productFlow) ? (
          <CommonFormFields
            formEntry={formEntry}
            onFormEntryChange={setFormEntry}
            touched={touched}
            errors={errors}
            onFieldBlur={onFieldBlur}
            activityType="extraction"
            availableMaterialIds={getAvailableMaterialIds()}
          />
        ) : (
          <CommonFormFields
            formEntry={formEntry}
            onFormEntryChange={setFormEntry}
            touched={touched}
            errors={errors}
            onFieldBlur={onFieldBlur}
            activityType="extraction_and_transformation"
            availableMaterialIds={getAvailableMaterialIds()}
          />
        )}

        {/* Dialog Footer */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            disabled={
              !isFormValid(formEntry) ||
              !!(formEntry.usesExternalStone === "yes" &&
                formEntry.comesFromAssociated === "yes" &&
                formEntry.originCompanyId &&
                !formEntry.originProductId)
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {editing ? t("common.saveChanges") : t("common.add")}
          </Button>
        </div>
      </div>
    </div>
  );
}
